import { useState, useEffect, useRef } from "react";
import searchIcon from "../../assets/images/search.svg";
import { getAsset, getLabel } from "../../utils/appConfig";
import { IoClose } from "react-icons/io5";
import { FaCheck } from "react-icons/fa";
import { getSpecialties, saveUserPreferences } from "./service";
import { useNavigate, useLocation } from "react-router-dom";
import { getDecodedToken } from "../../utils/localStorage";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import useStore from "../../store/useStore";
import { trackZymedEvent, ZYMED_EVENTS } from "../../utils/zymedAnalytics";

// Add inline CSS for the animation
const animationStyles = `
  @keyframes moveToTop {
    0% { 
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 50;
    }
    70% {
      position: absolute;
      top: 12px;
      left: 16px;
      transform: translate(0, 0);
      z-index: 50;
    }
    100% { 
      position: absolute;
      top: 12px;
      left: 16px;
      transform: translate(0, 0);
      z-index: 50;
    }
  }
  .logo-container {
    position: relative;
    width: 100%;
    height: 100vh;
    overflow: hidden;
  }
  .logo-splash {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 50;
  }
  .logo-animating {
    position: absolute;
    animation: moveToTop 0.6s cubic-bezier(0.42, 0, 0.58, 1) forwards;
    z-index: 50;
  }
  .logo-header {
    position: relative;
  }
`;

const PersonalizeFeed = () => {
  const doctorDetails: any = getDecodedToken();
  const { user } = doctorDetails || {};
  const location = useLocation();
  const { state } = location;
  const activeTab = state?.activeTab || "latest";
  const [stage, setStage] = useState("splash");
  const [searchText, setSearchText] = useState("");
  const [selectedSpecialties, setSelectedSpecialties] = useState<any[]>([]);
  const logoRef = useRef(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();
  const [specialties, setSpecialties] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const {
    pediaUserPreferences,
    setPediaUserPreferences,
  } = useStore();

  // Get the appropriate logo and label based on app configuration
  const pediaLogo = getAsset("pedia");
  const pediaLabel = getLabel("pediaLabel");

  const organizeSpecialties = (specialtiesList: any[]) => {
    if (!specialtiesList || specialtiesList.length === 0) return [];
    const selected = specialtiesList.filter((spec) =>
      selectedSpecialties.some((s) => s.id === spec.id)
    );
    const unselected = specialtiesList.filter((spec) =>
      !selectedSpecialties.some((s) => s.id === spec.id)
    );
    const sortByName = (a: any, b: any) => 
      (a?.displayName || '').localeCompare(b?.displayName || '');
    const sortedSelected = selected.sort(sortByName);
    const sortedUnselected = unselected.sort(sortByName);
    return [...sortedSelected, ...sortedUnselected];
  };

  const filteredSpecialties = searchText
    ? organizeSpecialties(specialties.filter((spec) =>
        spec?.displayName?.toLowerCase().includes(searchText.toLowerCase())
      ))
    : organizeSpecialties(specialties);

  useEffect(() => {
    const fetchSpecialitiesData = async () => {
      try {
        const specialtiesData = await getSpecialties();
        setSpecialties(specialtiesData?.speciality);
      } catch (error) {
        console.error("Error fetching specialties:", error);
      }
    };
    fetchSpecialitiesData();
  }, []); 

  useEffect(() => {
    if (specialties.length > 0 && selectedSpecialties.length === 0) {
      const extractSpecialityIds = (specialitiesString: string) => {
        if (!specialitiesString) return [];
        const match = specialitiesString.match(/speciality_id:\((.*?)\)/);
        if (!match || !match[1]) return [];
        return match[1].split("OR").map((id) => parseInt(id.trim()));
      };
      const specialityIds = extractSpecialityIds(pediaUserPreferences?.specialities);
      const selectedSpecs = specialties.filter(
        (s: any) => specialityIds.includes(s?.id)
      ) || [];
      setSelectedSpecialties(selectedSpecs);
    }
  }, [specialties, pediaUserPreferences]);

  useEffect(() => {
    if (stage === "splash") {
      const timer = setTimeout(() => {
        setStage("animating");

        setTimeout(() => {
          setStage("personalize");
        }, 500);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [stage]);

  const toggleSpecialty = (specialty: any) => {
    const wasSelected = selectedSpecialties.some((s) => s.id === specialty.id);
    trackZymedEvent(ZYMED_EVENTS.PEDIA_SPECIALTY_SELECTED, {
      specialty_name: specialty?.name ?? specialty?.displayName ?? specialty?.code,
      is_selected: !wasSelected,
    });
    if (selectedSpecialties.some((s) => s.id === specialty.id)) {
      setSelectedSpecialties(
        selectedSpecialties.filter((s) => s.id !== specialty.id)
      );
    } else {
      setSelectedSpecialties([...selectedSpecialties, specialty]);
    }
  };

  const isAllSelected = () => {
    return specialties.length > 0 && selectedSpecialties.length === specialties.length;
  };

  const handleSelectAllToggle = () => {
    if (isAllSelected()) {
      setSelectedSpecialties([]);
    } else {
      setSelectedSpecialties([...specialties]);
    }
  };

  const handleGetStarted = async () => {
    try {
      setLoading(true);
      const tokenData: any = getDecodedToken();
      let specialitiesQuery = "";
      if (selectedSpecialties.length > 0) {
        specialitiesQuery = selectedSpecialties
          .map((specialty) => specialty.id)
          .join(" OR ");
      }

      const payload = {
        userId: tokenData?.user?.b2cId,
        specialities: specialitiesQuery ? `speciality_id:(${specialitiesQuery})` : "",
      };

      const response = await saveUserPreferences(payload);
      if (response?.status === 200) {
        trackZymedEvent(ZYMED_EVENTS.PEDIA_PERSONALISE_FEED_SAVED, {
          selected_specialties: selectedSpecialties
            .map((specialty) => specialty?.name ?? specialty?.displayName ?? specialty?.code)
            .filter(Boolean),
        });
        setPediaUserPreferences(payload);
        const redirectPath = activeTab && activeTab !== "latest" 
          ? `/tatvapedia?activeTab=${encodeURIComponent(activeTab)}`
          : "/tatvapedia";
        navigate(redirectPath);
      }
    } catch (error) {
      console.error("Error saving user preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    // Force re-render on iOS WebView
    if (searchInputRef.current) {
      searchInputRef.current.style.opacity = "0.99";
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.style.opacity = "1";
        }
      }, 10);
    }
  };

  const handleSearchFocus = () => {
    setIsFocused(true);
    // Delay to ensure WebView handles focus properly
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
        // Force cursor to end of input
        const length = searchInputRef.current.value.length;
        searchInputRef.current.setSelectionRange(length, length);
      }
    }, 100);
  };

  const handleSearchBlur = () => {
    setIsFocused(false);
  };

  // Splash screen
  if (stage === "splash") {
    return (
      <div className="flex flex-col min-h-screen bg-white">
        {/* Add the style tag for animations */}
        <style>{animationStyles}</style>

        {/* Splash screen content */}
        <div className="logo-container">
          <div className="logo-splash" ref={logoRef}>
            <img src={pediaLogo} alt={pediaLabel} className="h-12" />
          </div>
        </div>
      </div>
    );
  }

  // Animation stage
  if (stage === "animating") {
    return (
      <div className="flex flex-col min-h-screen bg-white">
        {/* Add the style tag for animations */}
        <style>{animationStyles}</style>

        {/* Animating logo */}
        <div className="logo-container">
          <div className="logo-animating">
            <img src={pediaLogo} alt={pediaLabel} className="h-10" />
          </div>
        </div>
      </div>
    );
  }

    // Personalize view
    return (
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => {
            const redirectPath = activeTab && activeTab !== "latest" 
              ? `/tatvapedia?activeTab=${encodeURIComponent(activeTab)}`
              : "/tatvapedia";
            navigate(redirectPath);
          }}
        />
        
        {/* Bottom Sheet Modal */}
        <div className="fixed bottom-0 left-0 right-0 w-full bg-white rounded-t-3xl z-50 min-h-screen overflow-hidden animate-slide-up max-w-[1280px] mx-auto">
          {/* Drag Handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
          </div>

          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 mb-1">
                  Personalise your feed
                </h2>
                <p className="text-sm text-gray-500">
                  {!pediaUserPreferences?.specialities 
                    ? "You'll see based on your selected specialties, and personalize your feed anytime."
                    : "You'll see content based on your selected specialties."
                  }
                </p>
              </div>
              <button
                onClick={() => {
                  const redirectPath = activeTab && activeTab !== "latest" 
                    ? `/tatvapedia?activeTab=${encodeURIComponent(activeTab)}`
                    : "/tatvapedia";
                  navigate(redirectPath);
                }}
                className="w-8 h-8 bg-[#98A2B3] rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <IoClose size={26} className="text-white" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-6 py-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search speciality"
                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none placeholder:text-gray-400"
                value={searchText}
                onChange={handleSearchInputChange}
                ref={searchInputRef}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                style={{
                  WebkitAppearance: "none",
                  WebkitUserSelect: "text",
                  userSelect: "text",
                  transform: "translateZ(0)",
                  WebkitTransform: "translateZ(0)",
                  willChange: "contents",
                  opacity: isFocused ? 0.99 : 1,
                  color: searchText ? "#374151" : "#9CA3AF",
                }}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
              />
              <img
                src={searchIcon}
                alt="search"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
              />
            </div>
          </div>

          {/* Select All Button */}
          <div className="px-6 pb-2">
            <button
              onClick={handleSelectAllToggle}
              className="text-[#4B4AD5] hover:text-[#3D3BC4] text-sm font-medium underline transition-colors"
            >
              {isAllSelected() ? "Unselect All" : "Select All"}
            </button>
          </div>

          {/* Specialty List */}
          <div className="px-6 pb-4 flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 340px)' }}>
            <div className="space-y-3">
              {filteredSpecialties.map((specialty) => (
                <div
                  key={specialty.id}
                  className={`flex justify-between items-center p-4 rounded-2xl border transition-colors cursor-pointer ${
                    selectedSpecialties.some((s) => s.id === specialty.id)
                      ? "border-[#3D8C40] bg-[#EDF7ED]"
                      : "border-[#DCE4E8] bg-white"
                  }`}
                  onClick={() => toggleSpecialty(specialty)}
                >
                  <span
                    className={`font-medium ${
                      selectedSpecialties.some((s) => s.id === specialty.id)
                        ? "text-[#3D8C40]"
                        : "text-[#5A6774]"
                    }`}
                  >
                    {specialty?.displayName}
                  </span>
                  {selectedSpecialties.some((s) => s.id === specialty.id) ? (
                    <div className="w-6 h-6 bg-[#3D8C40] rounded-full flex items-center justify-center">
                      <FaCheck size={12} className="text-white" />
                  </div>
                  ) : (
                    <div className="w-6 h-6 border-2 border-[#6C7278] rounded-full"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-100">
            <button
              className={`w-full py-4 rounded-xl font-semibold transition-colors ${
                selectedSpecialties.length === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#4B4AD5] text-white"
              }`}
              disabled={selectedSpecialties.length === 0 || loading}
              onClick={handleGetStarted}
            >
              {loading ? (
                <Spin indicator={<LoadingOutlined spin className="text-white" />} />
              ) : (
                !pediaUserPreferences?.specialities ? "Get Started" : "Save"
              )}
            </button>
          </div>
        </div>

        <style>{`
          @keyframes slide-up {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
        `}</style>
      </>
    );
};

export default PersonalizeFeed;
